import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import { useState } from "react";
import type { CatalogBook } from "../../../helpers/BookCatalog";
import useBreakpoints from "../../../hooks/useBreakpoints";

/** Enough to fill a few rows; the rest stays one click (or a search) away. */
const VISIBLE_BOOKS_LIMIT = 12;

interface BookGridProps {
    books: Array<CatalogBook>;
    selectedBookId?: string;
    /** For books of several languages, e.g. search results. */
    isLanguageShown?: boolean;
    onPick: (book: CatalogBook) => void;
}

/** The books' covers to pick from, the first dozen at first. */
const BookGrid = (props: BookGridProps) => {
    const { isAboveLG } = useBreakpoints();
    const t = useT();
    const t_showLess = t("Show less", { _tags: "CreateTest,SelectBook" });

    // Opened expanded when an earlier pick lies beyond the limit, so it stays visible.
    const [isExpanded, setIsExpanded] = useState(
        () => props.books.findIndex((book) => book.id === props.selectedBookId) >= VISIBLE_BOOKS_LIMIT
    );
    const visibleBooks = isExpanded ? props.books : props.books.slice(0, VISIBLE_BOOKS_LIMIT);
    const hiddenCount = props.books.length - VISIBLE_BOOKS_LIMIT;
    const CaretIcon = isExpanded ? CaretUpIcon : CaretDownIcon;

    const renderCard = (book: CatalogBook) => {
        const isSelected = book.id === props.selectedBookId;
        // Volumes share one cover; the count shows there are more.
        const details = [
            book.publisherName,
            props.isLanguageShown && book.subjectName,
            book.volumes.length > 1 &&
                t("{count} volumes", { _tags: "CreateTest,SelectBook", count: book.volumes.length }),
        ].filter(Boolean);

        return (
            <button
                key={book.id}
                type="button"
                onClick={() => props.onPick(book)}
                aria-pressed={isSelected}
                className="group flex cursor-pointer flex-col gap-xs border-none bg-transparent p-no text-left"
                data-testid="book-selection-book-card"
            >
                <span
                    className={`relative flex aspect-3/4 w-full overflow-hidden rounded-xsmall-container border border-solid bg-background-light shadow-sm transition-[transform,box-shadow] group-hover:-translate-y-xxs group-hover:shadow-md ${
                        isSelected ? "border-creation-primary ring-2 ring-creation-primary" : "border-p6-grey-lightest"
                    }`}
                >
                    {book.coverImage ? (
                        <img
                            src={book.coverImage}
                            alt=""
                            loading="lazy"
                            className="size-full object-cover"
                        />
                    ) : (
                        <span className="p-s text-l font-bold text-p6-grey-dark">{book.bookName}</span>
                    )}
                </span>
                <span className="flex flex-col gap-xxs">
                    <span className="text-m font-medium text-p6-grey-dark">{book.bookName}</span>
                    <span className="text-s text-p6-grey-mid">{details.join(" · ")}</span>
                </span>
            </button>
        );
    };

    return (
        <div className="flex flex-col gap-s">
            <div
                className={`grid ${
                    isAboveLG
                        ? "grid-cols-[repeat(auto-fill,minmax(var(--size-cover-card),1fr))] gap-m"
                        : "grid-cols-[repeat(auto-fill,minmax(var(--size-cover-card-compact),1fr))] gap-s"
                }`}
            >
                {visibleBooks.map(renderCard)}
            </div>
            {hiddenCount > 0 && (
                <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    aria-expanded={isExpanded}
                    className="flex cursor-pointer items-center gap-xs self-center rounded-button border-2 border-solid border-creation-primary bg-white px-m py-xs text-m font-medium text-creation-primary hover:bg-creation-primary-light-pastel"
                    data-testid="book-selection-show-more"
                >
                    {isExpanded
                        ? t_showLess
                        : t("Show more ({count})", { _tags: "CreateTest,SelectBook", count: hiddenCount })}
                    <CaretIcon aria-hidden="true" />
                </button>
            )}
        </div>
    );
};

export default BookGrid;
